import { useCallback } from 'react';

import { useSnackbarNotifications as useSnackbarNotificationsContext } from './snackbarNotifications.context';

import type { SnackbarKey } from 'notistack';

export { SnackbarNotificationsProvider } from './snackbarNotifications.context';
export type { SnackbarMessage } from './snackbarNotifications.context';
export { useSnackbarNotificationsContext };

/**
 * Legacy hook for backward compatibility.
 * Uses snackIdRef to track a single notification at a time.
 *
 * For new code, consider using useSnackbarNotificationsContext
 * with requestKey pattern to manage multiple concurrent notifications.
 */
type SnackbarNotificationsResult = {
  showErrorSnackbar: (message: string, autoHideDuration?: number, key?: SnackbarKey) => SnackbarKey;
  showLoadingSnackbar: (message: string, key?: SnackbarKey) => SnackbarKey;
  showSuccessSnackbar: (message: string, key?: SnackbarKey) => SnackbarKey;
};

const useSnackbarNotifications = (): SnackbarNotificationsResult => {
  const contextFunctions = useSnackbarNotificationsContext();

  const showErrorSnackbar = useCallback(
    // eslint-disable-next-line sonarjs/function-return-type
    (message: string, autoHideDuration?: number, key?: SnackbarKey): SnackbarKey => {
      // Use provided key or generate a default one
      const requestKey = key?.toString() || 'default';
      return contextFunctions.showErrorSnackbar(requestKey, message, autoHideDuration);
    },
    [contextFunctions]
  );

  const showLoadingSnackbar = useCallback(
    // eslint-disable-next-line sonarjs/function-return-type
    (message: string, key?: SnackbarKey): SnackbarKey => {
      const requestKey = key?.toString() || 'default';
      return contextFunctions.showLoadingSnackbar(requestKey, message);
    },
    [contextFunctions]
  );

  const showSuccessSnackbar = useCallback(
    // eslint-disable-next-line sonarjs/function-return-type
    (message: string, key?: SnackbarKey): SnackbarKey => {
      const requestKey = key?.toString() || 'default';
      return contextFunctions.showSuccessSnackbar(requestKey, message);
    },
    [contextFunctions]
  );

  return {
    showErrorSnackbar,
    showLoadingSnackbar,
    showSuccessSnackbar
  };
};

export default useSnackbarNotifications;
